<template>
    <Teleport to="body">
        <Dialog 
            :show="showConfirmDialog"
            :size="'small'"
            :buttons="confirmDialogButtons"
            @close="showConfirmDialog = false"
            @confirm="cancelReservation()"
            >
            <template #header>
                {{$t('reservation.cancel_reservation_header')}} #{{ reservation.reservation_id }}
            </template>
            <template #body>
                {{$t('reservation.cancel_reservation_body_text', {number: reservation.reservation_id})}}
            </template>
        </Dialog>
    </Teleport>
    <div class="reservation-wrapper mb-6">
        <div class="reservation-component text-none pa-0 pa-md-6 pb-0 mb-0 mb-md-0" v-if="reservation">

            <div class="d-flex flex-row">
                <div class="payment-list-checkbox mr-4" v-if="reservation.status == 'CREATED'">
                    <label class="container input-label">
                        <!-- {{$t('reservation.add_to_payment_label')}} -->
                        <input type="checkbox"
                            :checked="isInPaymentReservations"
                            @click="togglePaymentReservationsItem()"
                        >
                        <span class="checkmark"></span>
                    </label>
                </div>

                <div class="reservation-component-container">
                    <div class="number-wrapper text-left d-flex flex-column">
                        <span class="d-flex align-center mb-2 mb-md-0">
                            <img :src="box_icon" class="mr-3" />{{ $t('reservation.number_label') }} #{{ reservation.reservation_id }}
                        </span>

                        <div v-if="$vuetify.display.mobile" class="date-wrapper text-right d-flex align-center">
                            <img :src="calendar_icon" class="mr-3" />{{ $t('reservation.creation_date_label') }} {{ getDate(reservation.reservation_time) }}
                        </div>
                    </div>
                    <div v-if="!$vuetify.display.mobile" class="date-wrapper text-right d-flex align-center">
                        <img :src="calendar_icon" class="mr-3" />{{ $t('reservation.creation_date_label') }} {{ getDate(reservation.reservation_time) }}
                    </div>
                    <div class="data-row d-flex flex-md-row flex-column pt-6 pr-0 pr-md-4 pl-0 pl-md-8">
                        <div class="package-name-wrapper mb-6 mb-md-0">
                            <div class="label mb-2 text-left">
                                {{ $t('reservation.package_type_label') }}
                            </div>
                            <div class="package-name-data text-left">
                                {{ getName() }}
                            </div>
                            <div class="text-left sub-price">
                                {{ $t('reservation.donor_nickname_label') }}
                                <span class="donor-id-data px-2 ml-2 mt-2">
                                    {{ reservation.reservation_data.donor_nickname }}
                                </span>
                            </div>
                        </div>
                        <v-spacer v-if="!$vuetify.display.mobile"></v-spacer>

                        <div class="quantity-wrapper mr-md-16 mr-0 mb-6 mb-md-0 w-50">
                            <div class="label mb-2 text-left text-md-right">
                                {{ $t('reservation.quantity_label') }}
                            </div>
                            <div class="quantity-data text-md-right text-left">
                                {{ reservation.reservation_data.quantity }}
                            </div>
                        </div>

                        <v-spacer v-if="!$vuetify.display.mobile"></v-spacer>
                        <div class="price-wrapper mr-md-16 mr-0 mb-6 mb-md-0 w-50">
                            <div class="label mb-2 text-md-right text-left">
                                {{ $t('reservation.summary_label') }}
                            </div>
                            <div class="price-data text-md-right text-left">
                                <span :class="['price', reservation.reservation_data?.mz_grant ? 'no-refund' : '']">
                                    {{ getValue(false) }}
                                </span>
                                <span v-if="reservation.reservation_data?.mz_grant" class="price ml-2">
                                    {{ getValue(true) }}
                                </span>
                                <div class="sub-price ml-0 mt-2">({{ getUnitPrice() }} {{$t('reservation.price_for_package_label')}})</div>
                            </div>
                            <div 
                                v-if="reservation.reservation_data?.mz_grant"
                                :class="['mz-package-label px-1 py-1 mt-2 d-flex', {'mt-6': $vuetify.display.mobile, 'f-right': !$vuetify.display.mobile}]">
                                <img :src="paidIcon" class="mr-1"/> {{ $t('mz.one_refund_package_label') }}
                            </div>
                        </div>

                        <v-spacer v-if="!$vuetify.display.mobile"></v-spacer>
                        <div class="status-wrapper ml-md-4 ml-0">
                            <div class="label mb-2 text-left">
                                {{ $t('reservation.status_label') }}
                            </div>
                            <div class="price-data text-left d-flex flex-row flex-md-column align-start">
                                <div :class="['status px-3 py-1 mb-md-2 mb-8', reservation.status]">
                                    {{ this.$t(`status.${reservation.status}`) }}</div>
                                <div class="ml-4 ml-md-0 mt-md-0 mt-1 invicta" :class="$vuetify.display.mobile ? 'timer' : ''" v-if="reservation.status == 'CREATED'">
                                    <Tooltip
                                        :text="$t('reservation.timer_tooltip_text')"
                                        :icon="false"
                                        :class="this.$vuetify.display.mobile ? 'tooltip-mobile' : '' ">
                                        <timer-component ref="timer" :date="reservation.expiration_time || ''"
                                            :on="reservation.status == 'CREATED'" />
                                    </Tooltip>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div :class="['mb-8 d-flex flex-md-row flex-column justify-space-between align-md-center']">

            <div :class="['ml-0 ml-md-12', $vuetify.display.mobile ? 'w-100' : 'w-45']">
                <div class="d-flex flex-md-row flex-column align-md-center align-start">
                    <div class="text-left mb-md-0 mb-2 w-auto panel" v-if="reservation.reservation_data?.product_key">
                        <compare-to-donor-component 
                            :donor_phenotype="{}"
                            :donor_id="reservation.reservation_data.donor_id"
                            :donor_nickname="reservation.reservation_data.donor_nickname ?? ''"
                            :donor_product_key="reservation.reservation_data.product_key"
                            :donor_material_type="reservation.reservation_data.material_type"
                            :user_id="oidcUser.user_id"
                            :oidc_user="oidcUser"
                            :reservation_id="reservation.reservation_id"
                            :key="reservation.reservation_id"
                            :type="'reservation'"
                            :action="null"
                            @compare-dialog="hasOpenCompareDialog">
                        </compare-to-donor-component>
                    </div>
                </div>
            </div>

            <div>
                <v-btn
                    v-if="reservation.status == 'CREATED'"
                    class="button py-2 px-6 mr-4 mt-md-0 mt-4"
                    @click="showConfirmDialog = true"
                    :disabled="isInPaymentReservations"
                    :width="$vuetify.display.mobile ? '100%' : 'unset'"
                    height="40"
                    color="error"
                    variant="flat"
                    rounded>
                        {{$t('reservation.cancel_reservation_btn')}}
                </v-btn>

                <v-btn
                    v-if="['CREATED','PAYMENT_IN_PROGRESS'].includes(reservation.status)"
                    class="button py-2 px-6 mr-4 mt-md-0 mt-4"
                    @click="payForReservation()"
                    :disabled="fetchingPaymentLink || isInPaymentReservations"
                    :width="$vuetify.display.mobile ? '100%' : 'unset'"
                    height="40"
                    color="primary"
                    variant="flat"
                    rounded>
                        {{$t('reservation.pay_for_reservation_btn')}}
                    <v-icon
                        v-if="fetchingPaymentLink"
                        size="16"
                        icon="mdi-loading mdi-spin"
                        color="white"
                        class="ml-2"
                    ></v-icon>
                </v-btn>
            </div>

        </div>

        <div v-if="reservation.status == 'FINISHED'" class="mb-md-4 mb-6 text-left invicta w-100"
            :class="$vuetify.display.mobile ? 'deposit-info-mobile' : 'deposit-info'">
            <Notification
                :type="'info'"
                :action="{label: $t('reservation.deposits_notification_action_label'), callback: goToDeposits}"
                :text="$t('reservation.deposits_notification_text')"
            /><br/>
        </div>
    </div>
</template>

<script>
import { computed } from "vue";
import { useStore } from 'vuex';
import axios from "axios";
import TimerComponent from "./TimerComponent.vue";
import CompareToDonorComponent from './CompareToDonorComponent.vue';
import reservationUtil from "@/utils/reservation_util";
import { Notification, Tooltip, Dialog } from "design-system-ui-components";

export default {
    name: "reservation-component",
    data: () => {
        return {
            fetchingPaymentLink: false,
            showConfirmDialog: false,
            compareDialog: false,
        }
    },
    props: {
        reservation: {
            type: Object
        }
    },
    emits: ['refresh', 'compare-dialog'],
    watch: {
        compareDialog(d) {
            this.$emit('compare-dialog', d)
        }
    },
    components: {
        CompareToDonorComponent,
        TimerComponent,
        Notification,
        Tooltip,
        Dialog
    },
    setup() {
        const store = useStore()
        const currentUrl = window.location.href

        const box_icon = computed({
            get: () => { return require('../assets/icons/box.svg'); }
        })
        const calendar_icon = computed({
            get: () => { return require('../assets/icons/calendar.svg'); }
        })
        const paidIcon = computed({
        get: () => { return require('@/assets/icons/paid.svg');}
        })

        return {
            box_icon,
            calendar_icon,
            paidIcon,
            currentUrl,
            paymentReservations: computed(() => store.getters['reservationModule/getPaymentReservations']),
            oidcUser: computed(() => store.getters['oidcStore/oidcUser'])
        }
    },
    computed: {
        isInPaymentReservations() {
            return this.paymentReservations.find(i => i.reservation_id == this.reservation.reservation_id)
        },
        confirmDialogButtons() {
            return [
                {
                    type: 'Primary',
                    text: this.$t('reservation.cancel_reservation_confirm_btn'),
                    event_name: 'confirm'
                },
                {
                    type: 'Secondary',
                    text: this.$t('reservation.cancel_reservation_close_btn'),
                    event_name: 'close'
                }
            ]
        }
    },
    methods: {
        hasOpenCompareDialog(d) {
            this.compareDialog = d
        },
        getDate(date) {
            let year = date.slice(0, 4)
            let month = date.slice(5, 7);
            let day = date.slice(8, 10);
            return day + '.' + month + '.' + year;
        },
        getName() {
            return this.reservation.reservation_data.product_name;
        },
        getValue(discount) {
            return this.$n(
                discount
                ? 0
                : this.reservation.reservation_data.quantity * this.reservation.reservation_data.price,
                'currency',
                'pl'
            );
        },
        getUnitPrice() {
            if (this.reservation.reservation_data.price)
                return this.$n(this.reservation.reservation_data.price, 'currency', 'pl');
            else
                return 0;
        },
        cancelReservation() {
            axios.delete('/api/reservation?reservation_id=' + this.reservation.reservation_id)
            .then(() => {
                this.$root.$alertMessageRef.showAlert(
                    this.$t('reservation.cancel_reservation_success_msg', {number: this.reservation.reservation_id}),
                    null,
                    {
                        link: false,
                        action: false,
                        timeout: true,
                        timeoutDuration: 3000,
                        color: 'rgba(32, 30, 76, 1)',
                        backgroundColor: 'rgba(107, 215, 131, 0.2)',
                        borderColor: 'rgba(107, 215, 131, 1)',
                        icon: 'mdi-check-circle-outline',
                        iconColor: 'rgba(107, 215, 131, 1)',
                        width: '800px'
                    }
                )
                this.$gtag.event('donor', {
                  'donor_product_key': this.reservation.reservation_data.product_key,
                  'donor_material_type': this.reservation.reservation_data.material_type,
                  'donor_event_value': 'canceled reservation by user'
                })
                this.$emit('refresh')
            })
            .catch(err => {
                console.error(err);
                this.$root.$alertMessageRef.showAlert(
                    this.$t('reservation.cancel_reservation_failure_msg', {number: this.reservation.reservation_id}),
                    null,
                    {
                        link: false,
                        action: false,
                        timeout: true,
                        timeoutDuration: 3000,
                        color: 'rgba(32, 30, 76, 1)',
                        backgroundColor: 'rgba(232, 69, 69, 0.2)',
                        borderColor: 'rgba(232, 69, 69, 1)',
                        icon: 'mdi-close-circle-outline',
                        iconColor: 'rgba(232, 69, 69, 1)',
                        width: '800px'
                    }
                )
            })
            .finally(() => {
                this.showConfirmDialog = false;
            })
        },
        payForReservation() {
            this.fetchingPaymentLink=true;
            if(!this.reservation.payment_data.payment_url) {
                axios.put(`/api/reservation/payment`, {
                    selected_reservations: [ this.reservation.reservation_id ],
                    return_url: reservationUtil.getPaymentReturnUrlWithParameters([this.reservation], this.$i18n.locale)
                })
                .then(response => {
                    if (response.data.payment_url && response.data.already_paid != true) {
                        window.location.href = response.data.payment_url
                    } else {
                        this.fetchingPaymentLink=false
                        this.$emit('refresh')
                    }
                })
                .catch(error => {
                    this.fetchingPaymentLink=false
                    if (error.response) {
                        console.error(error.response.data)
                    } else {
                        console.error("PAYMENT ERROR:",error)
                    }
                })
            } else {
                window.location.href = this.reservation.payment_data.payment_url;
            }
        },
        togglePaymentReservationsItem() {
            this.$store.dispatch('reservationModule/togglePaymentReservationsItem', this.reservation)
        },
        goToDetails() {
            this.$router.push({
                name: 'donorDetails',
                params: {
                    materialType: this.reservation.reservation_data.material_type == 'mtCell' ? 'eggs' : 'sperm',
                    id: this.reservation.reservation_data.product_key
                }
            })
        },
        goToDeposits() {
            this.$router.push({
                name: 'dashboardView',
                query: {
                    selected: 'deposits'
                }
            })
        },
        goToMedipoint() {
            this.$router.push({
                name: 'redirect',
                params: {
                    mode: 'simple',
                    pathname: location.pathname,
                    url: `${process.env.VUE_APP_MEDIPOINT_URL}/${this.$i18n.locale}/dane-medyczne/depozyty`
                }
            })
        },
    }
}
</script>

<style lang="scss" scoped>
@import "../../node_modules/design-system-ui-components/dist/all.min.css";
.reservation-wrapper {
    border-bottom: 1px solid $nsb-color-3;

    .deposit-info {
        position: relative;
        width: auto;
        width: calc(100% - 30px) !important;
        padding-left: 50px;
    }
    .deposit-info-mobile {
        position: relative;
        width: auto;
        width: 100%;
    }
    :deep(.notification--icon) {
        min-width: 24px !important;
    }
    .link {
        font-weight: bold !important;
        cursor: pointer;
    }
    .panel {
        position: relative;
        border-radius: 12px;
        border: 1px solid transparent !important;
        cursor: pointer;
    }
    .panel:hover {
        border: 1px solid var(--nsb-color-1, #0E346F) !important;
    }
}
.payment-list-checkbox {
    .container {
        .checkmark {
            margin-top: 2px;
            border-radius: 2px;
            border: 1px solid $ds-primary;
        }
    }
}
.reservation-component-container {
    box-sizing: border-box;
    background: white;
    width: 100%;

    @include nsb-font-container;
    font-size: 14px;
    line-height: 21px;

    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-rows: repeat(3, auto);
    grid-column-gap: 0px;
    grid-row-gap: 0px;

    .timer_tooltip {
        min-width: 200px;
    }
    .tooltip-mobile {
        max-width: 200px;
    }

    .number-wrapper {
        grid-area: 1 / 1 / 2 / 2;
    }

    .date-wrapper {
        grid-area: 1 / 2 / 2 / 3;
    }

    .data-row {
        grid-area: 2 / 1 / 3 / 3;
    }

    .bottom-row {
        grid-area: 3 / 1 / 4 / 3;
    }

    .label {
        @include nsb-select-placeholder;
        font-size: 12px;
        line-height: 18px;
    }

    .button {
        @include nsb-font-button;
        font-size: 14px !important;
    }

    .sub-price {
        @include nsb-select-placeholder;
        font-size: 10px;
        line-height: 15px;
    }

    .donor-id-data {
        @include nsb-font-container;
        font-size: 12px;
        line-height: 21px;
        border-radius: 12px;
        background-color: $ds-primary-9;
        display: inline-block;
    }

    .status {
        @include nsb-font-container;
        color: $ds-primary;
        border-radius: 12px;
        font-size: 12px;
        line-height: 18px;
        display: inline-block;

        &.CANCELED {
            background: $sd-error-2;
        }

        &.CANCELED_BY_USER {
            background: $sd-error-2; 
        }

        &.CREATED {
            background: $ds-primary-8;
        }
        &.PAYMENT_IN_PROGRESS {
            background: $ds-primary-8;
        }

        &.ERROR {
            background: $sd-error-2;
        }

        &.FINISHED {
            background: $ds-success-2;
        }

        &.PROCESSED {
            background: $ds-secondary-4;
        }
    }

    .status-wrapper {
        min-width: 148px;
    }

    .package-name-wrapper {
        min-width: 185px;
    }

    .timer {
        position: relative;
        cursor: pointer;
        width: 100%;
    }
    .no-refund {
        line-height: normal;
        color: $ds-primary-100;
        font-size: 12px !important;
        text-decoration: line-through;
    }
    .price-with-refund {
        line-height: normal;
        color: $ds-primary;
        font-size: 16px !important;
    }
    .mz-package-label {
        @include nsb-font-container;
        line-height: normal;
        border-radius: 100px;
        background: #F0FFF3;
        color: #0DA531;
        width: max-content;
        &.f-right {
        float: right;
        }
    }
}

.input-label {
    @include nsb-font-container;
    font-size: 14px;
    line-height: 17px;
    cursor: pointer;
    color: $ds-primary;
}

/* The container */
.container {
    display: block;
    position: relative;
    padding-left: 24px;
    cursor: pointer;
    user-select: none;
    @include nsb-font-button-regular;
    font-size: 16px !important;
    line-height: 24px;
}

/* Hide the browser's default checkbox */
.container input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

/* Create a custom checkbox */
.checkmark {
  box-sizing: border-box;
  border: 1px solid #869AB7;
  position: absolute;
  top: 0;
  left: 0;
  height: 16px;
  width: 16px;
  background-color: white;
}

/* On mouse-over, add a grey background color */
.container:hover input ~ .checkmark {
  background-color: $nsb-disabled;
}

/* When the checkbox is checked, add a blue background */
.container input:checked ~ .checkmark {
  background-color: $ds-primary;
}

/* Create the checkmark/indicator (hidden when not checked) */
.checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

/* Show the checkmark when checked */
.container input:checked ~ .checkmark:after {
  display: block;
}

/* Style the checkmark/indicator */
.container .checkmark:after {
  left: 5px;
  top: 1px;
  width: 4px;
  height: 9px;
  border: solid white;
  border-width: 0 1px 1px 0;
  -webkit-transform: rotate(45deg);
  -ms-transform: rotate(45deg);
  transform: rotate(45deg);
}
</style>